import { Link } from 'react-router-dom'
import type { ReactNode } from 'react'

import { CompanyMonogram, CompanySpine, DueDateBadge, UserAvatar } from '@/shared/components/common'
import { useT } from '@/shared/i18n'
import { cn } from '@/shared/lib/cn'
import type { ProjectSummary } from '@/shared/types/domain'

export interface ProjectCardProps {
  project: ProjectSummary
  /** El ordinal 1..X del scope priorizado. `null` en los permanentes. */
  position: number | null
  handle?: ReactNode
  isDragging?: boolean
  className?: string
}

/** Tarjeta de proyecto del tablero: estado primero, nombre después. */
export function ProjectCard({
  project,
  position,
  handle,
  isDragging,
  className,
}: ProjectCardProps) {
  const t = useT()

  return (
    <article
      className={cn(
        // El padding sale del token de densidad, igual que las filas de tabla.
        // El conmutador de densidad no tocaba el tablero: las tarjetas medían
        // 82px en compacto y 82px en cómodo, así que el control mentía sobre su
        // alcance (QA funcional 7.5).
        // Sin `hairline`: la columna ya es una caja con borde, y la tarjeta
        // adentro dibujaba un segundo contorno a 8px del primero. La jerarquía
        // acá la hace la SUPERFICIE —que es lo que la dirección de diseño pide
        // en lugar de sombras— y deja un solo borde por columna en vez de uno
        // por tarjeta.
        'flex items-stretch gap-2 rounded-[var(--radius-card)] bg-surface-2/60',
        'p-[var(--cell-px)]',
        'transition-colors duration-[var(--motion-fast)] hover:bg-surface-2',
        isDragging && 'bg-surface-2',
        className,
      )}
    >
      <CompanySpine hue={project.company.color_hue} label={project.company.name} />

      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex items-center gap-1.5">
          {handle}
          <span className="num text-2xs text-text-3">
            {position === null ? (
              <span aria-label={t('board.card.noPriority')}>—</span>
            ) : (
              `#${position}`
            )}
          </span>
          <span className="flex-1" />
          <DueDateBadge date={project.next_due_date} />
        </div>

        <Link
          to={`/projects/${project.id}`}
          className="truncate text-xs text-text-1 hover:text-signal"
        >
          {project.name}
        </Link>

        <div className="flex min-w-0 items-center gap-1.5 text-2xs text-text-3">
          <CompanyMonogram name={project.company.name} hue={project.company.color_hue} />
          <span className="truncate">{project.company.name}</span>
          <span className="flex-1" />
          {project.overdue_tasks > 0 ? (
            <span
              className="num text-overdue"
              aria-label={t('board.card.overdueTasks', { count: project.overdue_tasks })}
            >
              <span aria-hidden="true">! </span>
              {project.overdue_tasks}
            </span>
          ) : null}
          <UserAvatar user={project.internal_owner} />
        </div>
      </div>
    </article>
  )
}
